import { useState, useEffect, useRef } from 'react';
import { onFirestoreError } from '../utils/firestoreErrors';

/**
 * Shows a banner when a Firestore write fails for quota or permission
 * reasons. The local change still made it into IndexedDB persistence,
 * so the app keeps working — the banner just lets the user know that
 * cross-device sync is currently degraded.
 *
 * Auto-dismisses after 12s OR when the user clicks ✕.
 */
export default function FirestoreErrorBanner() {
  const [error, setError] = useState(null);
  const dismissTimerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onFirestoreError(({ category }) => {
      // Ignore the noisy ones — user-cancelled writes don't matter.
      if (category === 'cancelled' || category === 'unknown') return;
      setError(category);
      // Clear any in-flight auto-dismiss before starting a fresh one,
      // so a new error doesn't get killed early by an older timeout.
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(() => setError(null), 12000);
    });
    return () => {
      unsubscribe();
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  if (!error) return null;

  const isQuota = error === 'quota';

  return (
    <div className="firestore-error-banner" role="alert">
      <div className="firestore-error-icon">{isQuota ? '⏳' : '🔒'}</div>
      <div className="firestore-error-body">
        <strong>
          {isQuota
            ? 'No pudimos sincronizar tus cambios ahora mismo.'
            : 'No tienes permisos para guardar este cambio.'}
        </strong>
        <span>
          {isQuota
            ? 'Tus cambios están guardados localmente. Se sincronizarán automáticamente en unos minutos.'
            : 'Si el problema persiste, recarga la página o vuelve a iniciar sesión.'}
        </span>
      </div>
      <button className="firestore-error-dismiss" onClick={() => setError(null)} aria-label="Cerrar aviso">✕</button>
    </div>
  );
}
